import React, { useState, useRef, useEffect } from 'react';
import './NotificationBell.css';
import { useDashboard } from '../../context/DashboardContext';
import api from '../../api';
import { formatTimeAgo } from '../../utils/dateUtils';
import { getEcho } from '../../echo';
import { notify } from '../CustomToast';
import { ReactComponent as BellIcon } from '../../assets/Bell.svg';
import { useI18n } from '../../i18n';
import { resolveNotificationNav } from '../../constants/notificationNavMap';
import { useChat } from '../../context/ChatContext';

const NotificationBell = () => {
    const { t } = useI18n();
    const { setIsNotificationsOpen, setActiveView, setDeepLink } = useDashboard();
    const { setActiveConversationId } = useChat();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [notifications, setNotifications] = useState([]);

    const fetchNotifications = async () => {
        try {
            const response = await api.get('/notifications?limit=10');
            if (response.status) {
                setNotifications(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    useEffect(() => {
        fetchNotifications();

        const userStr = localStorage.getItem('user');
        if (!userStr) return;

        const user = JSON.parse(userStr);
        const userId = user.id;

        if (userId) {
            const echo = getEcho();

            if (!echo) {
                console.error('Echo not initialized - user may not be authenticated');
                return;
            }

            echo.private(`App.Models.User.${userId}`)
                .notification((notification) => {
                    notify('info', t('common.notifications.new'), notification.msg || notification.act);

                    setNotifications(prev => [{
                        id: notification.id,
                        data: {
                            link: notification.link,
                            act: notification.act,
                            msg: notification.msg,
                            actor_name: notification.actor_name,
                            actor_avatar: notification.actor_avatar,
                            target_id: notification.target_id,
                            target_type: notification.target_type,
                            target_meta: notification.target_meta,
                        },
                        created_at: notification.created_at,
                        read_at: notification.read_at,
                    }, ...prev.slice(0, 9)]);
                })
                .error((error) => {
                    console.error('Failed to subscribe to notification channel:', error);
                });
        }

        return () => {
            if (userId) {
                const echo = getEcho();
                if (echo) {
                    echo.leave(`App.Models.User.${userId}`);
                }
            }
        };
    }, [t]);

    const hasUnread = notifications.some(n => !n.read_at);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleNotificationClick = async (notif) => {
        if (!notif.read_at) {
            try {
                await api.post(`/notifications/${notif.id}/read`);
                setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read_at: new Date().toISOString() } : n));
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        }

        const nav = resolveNotificationNav(notif);

        if (nav?.view) {
            setActiveView(nav.view);
            if (nav.deepLink?.target_type === 'conversation') {
                setActiveConversationId(nav.deepLink.target_id);
            } else if (nav.deepLink) {
                setDeepLink(nav.deepLink);
            }
            setIsOpen(false);
            return;
        }

        // Legacy fallback: link field (e.g. 'talk:42')
        if (notif.data?.link) {
            const [module, id] = notif.data.link.split(':');
            if (module === 'talk') {
                setActiveView('talk');
                if (id) setActiveConversationId(parseInt(id, 10));
                setIsOpen(false);
            }
        }
    };

    const handleSeeAll = () => {
        setIsOpen(false);
        setIsNotificationsOpen(true);
    };

    return (
        <div className="notification-bell-wrapper" ref={dropdownRef}>
            <div className="header-icon-container" onClick={() => setIsOpen(!isOpen)}>
                <BellIcon className="notification-bell-icon" />
                {hasUnread && <span className="icon-badge"></span>}
            </div>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h2>{t('navigation.notifications')}</h2>
                    </div>
                    <div className="notification-list">
                        {notifications.length > 0 ? (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.read_at ? 'unread' : ''}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-avatar-wrapper">
                                        {notification.data.actor_avatar
                                            ? <img src={notification.data.actor_avatar} alt={notification.data.actor_name || ''} className="notification-avatar" />
                                            : <div className="notification-avatar-initials">{(notification.data.actor_name || notification.data.act || '?').charAt(0)}</div>
                                        }
                                        <span className="status-dot"></span>
                                    </div>
                                    <div className="notification-content">
                                        <p className="notification-text">
                                            <strong>{notification.data.actor_name || notification.data.act}</strong>
                                        </p>
                                        <p className="notification-time">{notification.data.msg}</p>
                                        <div className="notification-project">
                                            {formatTimeAgo(notification.created_at)}
                                        </div>
                                    </div>
                                    {!notification.read_at && <div className="unread-indicator"></div>}
                                </div>
                            ))
                        ) : (
                            <div className="empty-notifications">
                                {t('common.notifications.none')}
                            </div>
                        )}
                    </div>

                    <div className="notification-footer">
                        <button className="see-all-btn" onClick={handleSeeAll}>{t('common.actions.show_all')}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
